import type { ActivityPair } from "@tandemdiabetes/sugarmate-api-react-client"
import { ActivityType } from "@tandemdiabetes/sugarmate-api-react-client"
import type {
  ComponentPropsWithoutRef,
  Ref,
} from "react"
import { forwardRef } from "react"

import { BolusActivityTitle } from "./BolusActivityTitle"
import { CGMSessionEndedActivityTitle } from "./CGMSessionEndedActivityTitle"
import { DeviceChangeActivityTitle } from "./DeviceChangeActivityTitle"
import { ExerciseActivityTitle } from "./ExerciseActivityTitle"
import { FoodActivityTitle } from "./FoodActivityTitle"
import { GlucoseActivityTitle } from "./GlucoseActivityTitle"
import { InsulinActivityTitle } from "./InsulinActivityTitle"
import { LiquidActivityTitle } from "./LiquidActivityTitle"
import { MedicationActivityTitle } from "./MedicationActivityTitle"
import { NoteActivityTitle } from "./NoteActivityTitle"
import { PumpSuspensionActivityTitle } from "./PumpSuspensionActivityTitle"
import type {
  ReadOnlySubEntry,
  ReadOnlyUserEvent,
} from "src/types"

export type Props = ComponentPropsWithoutRef<"div"> & ActivityPair & {
  item: ReadOnlySubEntry | ReadOnlyUserEvent;
}

export const ActivityTitle = forwardRef<HTMLDivElement, Props>(
  function ActivityTitle(props: Props, ref: Ref<HTMLDivElement>) {
    const {
      activity,
      activityType,
      item,
      ...htmlDivProps
    } = props

    switch (activityType) {
      case ActivityType.CGMSessionEnded:
        return (
          <CGMSessionEndedActivityTitle
            {...htmlDivProps}
            ref={ref}
            activity={activity}
          />
        )
      case ActivityType.DeviceChange:
        return (
          <DeviceChangeActivityTitle
            {...htmlDivProps}
            ref={ref}
            activity={activity}
            source={"source" in item ? item.source : null}
          />
        )
      case ActivityType.Exercise:
        return (
          <ExerciseActivityTitle
            {...htmlDivProps}
            ref={ref}
            activity={activity}
          />
        )
      case ActivityType.Food:
        return (
          <FoodActivityTitle
            {...htmlDivProps}
            ref={ref}
            activity={activity}
          />
        )
      case ActivityType.Glucose:
        return (
          <GlucoseActivityTitle
            {...htmlDivProps}
            ref={ref}
            activity={activity}
          />
        )
      case ActivityType.Liquid:
        return (
          <LiquidActivityTitle
            {...htmlDivProps}
            ref={ref}
            activity={activity}
          />
        )
      case ActivityType.Insulin:
        return (
          <InsulinActivityTitle
            {...htmlDivProps}
            ref={ref}
            activity={activity}
          />
        )
      case ActivityType.Medication:
        return (
          <MedicationActivityTitle
            {...htmlDivProps}
            ref={ref}
            activity={activity}
          />
        )
      case ActivityType.Note:
        return (
          <NoteActivityTitle
            {...htmlDivProps}
            ref={ref}
            item={item}
          />
        )
      case ActivityType.Bolus:
        return (
          <BolusActivityTitle
            {...htmlDivProps}
            ref={ref}
            activity={activity}
          />
        )
      case ActivityType.PumpSuspension:
        return (
          <PumpSuspensionActivityTitle
            {...htmlDivProps}
            ref={ref}
            activity={activity}
          />
        )
      default:
        return null
    }
  },
)
